import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { activateScene, createScene, createSceneAutomation, deleteScene } from "@/services/scenes";

const defaultScene = {
  name: "Filmabend",
  description: "Licht dimmen und den Raum vorbereiten.",
  actions: [
    { device_id: "", state: { power: "on", brightness: 20 } },
  ],
};

export const ScenePanel = ({ scenes, devices, token, onSceneChanged }) => {
  const [sceneDraft, setSceneDraft] = useState(defaultScene);
  const [automationTime, setAutomationTime] = useState("22:00");
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState("");

  const deviceOptions = useMemo(() => devices.slice(0, 20), [devices]);

  const updateDraft = (field, value) => setSceneDraft((prev) => ({ ...prev, [field]: value }));

  const updateAction = (field, value) => {
    setSceneDraft((prev) => ({
      ...prev,
      actions: prev.actions.map((action, index) => (index === 0 ? { ...action, [field]: field === "state" ? { ...action.state, ...value } : value } : action)),
    }));
  };

  const runScene = async (id) => {
    setPendingId(id);
    setError("");
    try {
      await activateScene(id, token);
      onSceneChanged?.();
    } catch (sceneError) {
      setError(sceneError.message || "Szene konnte nicht aktiviert werden");
    } finally {
      setPendingId(null);
    }
  };

  const removeScene = async (id) => {
    setPendingId(id);
    setError("");
    try {
      await deleteScene(id, token);
      onSceneChanged?.();
    } catch (sceneError) {
      setError(sceneError.message || "Szene konnte nicht gelöscht werden");
    } finally {
      setPendingId(null);
    }
  };

  const saveScene = async () => {
    setPendingId("create");
    setError("");
    try {
      await createScene(sceneDraft, token);
      setSceneDraft(defaultScene);
      onSceneChanged?.();
    } catch (sceneError) {
      setError(sceneError.message || "Szene konnte nicht erstellt werden");
    } finally {
      setPendingId(null);
    }
  };

  const buildAutomation = async (sceneId, sceneName) => {
    setPendingId(`${sceneId}-automation`);
    setError("");
    try {
      await createSceneAutomation(sceneId, { name: `${sceneName} at ${automationTime}`, if: { time: automationTime } }, token);
      onSceneChanged?.();
    } catch (sceneError) {
      setError(sceneError.message || "Automation konnte nicht erstellt werden");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section className="jarvis-panel jarvis-panel-span-full" data-testid="scene-panel">
      <div className="jarvis-panel-header">
        <div>
          <p className="jarvis-eyebrow">AION Szenensteuerung</p>
          <h2 className="jarvis-title" data-testid="scene-panel-title">Szenen & Automationen</h2>
        </div>
        <div className="jarvis-chip" data-testid="scene-panel-chip">{scenes.length} Szenen</div>
      </div>
      {error ? <div className="jarvis-inline-error" data-testid="scene-panel-error">{error}</div> : null}
      <div className="jarvis-scene-layout">
        <div className="jarvis-scene-list" data-testid="scene-panel-list">
          {scenes.map((scene) => (
            <div className="jarvis-scene-card" key={scene.id} data-testid={`scene-card-${scene.id}`}>
              <div>
                <strong>{scene.name}</strong>
                <small>{scene.description || "Keine Beschreibung"}</small>
              </div>
              <div className="jarvis-scene-actions" data-testid={`scene-actions-${scene.id}`}>
                <Button size="sm" onClick={() => runScene(scene.id)} disabled={pendingId === scene.id} data-testid={`scene-activate-button-${scene.id}`}>Aktivieren</Button>
                <Button size="sm" variant="outline" onClick={() => buildAutomation(scene.id, scene.name)} disabled={pendingId === `${scene.id}-automation`} data-testid={`scene-automation-button-${scene.id}`}>Regel erstellen</Button>
                <Button size="sm" variant="ghost" onClick={() => removeScene(scene.id)} disabled={pendingId === scene.id} data-testid={`scene-delete-button-${scene.id}`}>Löschen</Button>
              </div>
            </div>
          ))}
        </div>
        <div className="jarvis-scene-builder" data-testid="scene-builder-form">
          <h3>Szene erstellen</h3>
          <Input value={sceneDraft.name} onChange={(event) => updateDraft("name", event.target.value)} placeholder="Gute Nacht" data-testid="scene-builder-name-input" />
          <Input value={sceneDraft.description} onChange={(event) => updateDraft("description", event.target.value)} placeholder="Szenenbeschreibung" data-testid="scene-builder-description-input" />
          <label className="jarvis-field-label">
            Gerät
            <select value={sceneDraft.actions[0].device_id} onChange={(event) => updateAction("device_id", event.target.value)} data-testid="scene-builder-device-select">
              <option value="">Gerät auswählen</option>
              {deviceOptions.map((device) => (
                <option key={device.id} value={device.id}>{device.name}</option>
              ))}
            </select>
          </label>
          <div className="jarvis-scene-builder-grid">
            <Button size="sm" variant="outline" onClick={() => updateAction("state", { power: "on" })} data-testid="scene-builder-power-on">Einschalten</Button>
            <Button size="sm" variant="outline" onClick={() => updateAction("state", { power: "off" })} data-testid="scene-builder-power-off">Ausschalten</Button>
            <Button size="sm" variant="outline" onClick={() => updateAction("state", { power: "on", brightness: 20 })} data-testid="scene-builder-brightness-low">Dim</Button>
            <Button size="sm" variant="outline" onClick={() => updateAction("state", { power: "on", brightness: 70 })} data-testid="scene-builder-brightness-mid">Hell</Button>
          </div>
          <div className="jarvis-scene-builder-footer">
            <Input value={automationTime} onChange={(event) => setAutomationTime(event.target.value)} placeholder="22:00" data-testid="scene-builder-automation-time" />
            <Button onClick={saveScene} disabled={pendingId === "create"} data-testid="scene-builder-save-button">{pendingId === "create" ? "Speichert…" : "Szene erstellen"}</Button>
          </div>
        </div>
      </div>
    </section>
  );
};